const _decodeEntities = str => {
  if (!str) return '';
  const t = document.createElement('textarea');
  t.innerHTML = str;
  return t.value;
};

const _getCartUpdateUrl = () =>
  `${window.location.origin}${
    typeof currentLocal !== 'undefined' ? currentLocal : ''
  }/cart/update.js`;

const _getSectionsUrl = () =>
  typeof LOCALE_ROOT_URL !== 'undefined' ? LOCALE_ROOT_URL : '/';

class CartDiscountInput extends HTMLElement {
  connectedCallback() {
    if (document.body.classList.contains('tw-b2b')) return;
    if (this._initialized) return;
    this._initialized = true;

    this._source = this.dataset.source || 'slide_cart';
    this._errorInvalid =
      _decodeEntities(this.dataset.errorInvalid) ||
      'Enter a valid discount code';
    this._errorNotCombinable =
      _decodeEntities(this.dataset.errorNotCombinable) ||
      "couldn't be used with your existing discounts.";

    this._hasTypedThisSession = false;

    if (this._input()) {
      this._initAfterChildren();
    } else {
      requestAnimationFrame(() => this._initAfterChildren());
    }
  }

  _initAfterChildren() {
    this._appliedCodes = [
      ...this.querySelectorAll('.cart-discount-badge__code')
    ].map(el => el.textContent.trim().toUpperCase());

    this._restoreErrorState();
    this._bindEvents();
  }

  _restoreErrorState() {
    const stored = sessionStorage.getItem('cdi:error');
    if (!stored) return;
    try {
      const { code, message, type } = JSON.parse(stored);
      const input = this._input();
      if (input) input.value = code;
      if (type === 'banner') this._showBannerError(message);
      else this._showFieldError(message);
    } catch (_) {}
    sessionStorage.removeItem('cdi:error');
  }

  _bindEvents() {
    const input = this._input();
    const applyBtn = this._applyBtn();

    input?.addEventListener('input', () => {
      const sanitized = input.value.replace(/[^A-Za-z0-9_\-]/g, '');
      if (sanitized !== input.value) input.value = sanitized;

      if (!this._hasTypedThisSession && input.value.length > 0) {
        this._hasTypedThisSession = true;
        this._pushDataLayer('coupon_typing', { from: this._source });
      }
      if (input.value.length === 0) {
        this._hasTypedThisSession = false;
        this._hideErrors();
      }
      sessionStorage.removeItem('cdi:error');
    });

    input?.addEventListener('keydown', e => {
      if (e.key === 'Enter') applyBtn?.click();
    });

    applyBtn?.addEventListener('click', () => {
      const code = input?.value.trim().toUpperCase();
      if (!code) {
        this._showFieldError(this._errorInvalid);
        return;
      }
      this._pushDataLayer('coupon_name', {
        from: this._source,
        coupon_name: code
      });
      this._applyCode(code);
    });

    this.addEventListener('click', e => {
      const removeBtn = e.target.closest('.cart-discount-badge__remove');
      if (removeBtn) this._removeCode(removeBtn.dataset.code);
    });
  }

  async _applyCode(code) {
    const input = this._input();
    const applyBtn = this._applyBtn();

    this._hideErrors();
    this._setLoading(true, applyBtn);

    const allCodes = [...this._appliedCodes, code];
    const discountParam = allCodes.join(',');
    const fragmentName = this.dataset.sectionToUpdate || 'slide-cart-fragment';

    let cart;
    try {
      const resp = await fetch(_getCartUpdateUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          discount: discountParam,
          sections: fragmentName,
          sections_url: _getSectionsUrl()
        })
      });
      cart = await resp.json();
    } catch (_) {
      this._setLoading(false, applyBtn);
      this._showFieldError(this._errorInvalid);
      this._pushDataLayer('coupon_error', {
        from: this._source,
        coupon_name: code,
        error: 'network_error'
      });
      return;
    }

    this._setLoading(false, applyBtn);

    const validCodes = (cart.discount_codes || [])
      .filter(dc => dc.applicable)
      .map(dc => dc.code.toUpperCase());
    const codeApplied = validCodes.includes(code);

    try {
      localStorage.setItem(
        'cart:total_discount',
        String(cart.total_discount || 0)
      );
    } catch (_) {}

    if (codeApplied) {
      this._hasTypedThisSession = false;
      if (input) input.value = '';

      this._pushDataLayer('coupon_apply', {
        from: this._source,
        coupon_name: code
      });

      this.dispatchEvent(
        new CustomEvent('cart:discount:applied', {
          bubbles: true,
          detail: {
            sections: cart.sections,
            totalDiscount: cart.total_discount,
            cart
          }
        })
      );
    } else {
      this._pushDataLayer('coupon_error', {
        from: this._source,
        coupon_name: code,
        error: 'not_applicable'
      });

      const hasOtherCodes = validCodes.length > 0;
      const errorMessage = hasOtherCodes
        ? `${code} ${this._errorNotCombinable}`
        : this._errorInvalid;
      const errorType = hasOtherCodes ? 'banner' : 'field';

      try {
        sessionStorage.setItem(
          'cdi:error',
          JSON.stringify({ code, message: errorMessage, type: errorType })
        );
      } catch (_) {}

      this.dispatchEvent(
        new CustomEvent('cart:discount:applied', {
          bubbles: true,
          detail: {
            sections: cart.sections,
            totalDiscount: cart.total_discount,
            cart
          }
        })
      );
    }
  }

  async _removeCode(code) {
    const upperCode = code.toUpperCase();
    const remaining = this._appliedCodes.filter(
      c => c.toUpperCase() !== upperCode
    );
    const fragmentName = this.dataset.sectionToUpdate || 'slide-cart-fragment';

    this._pushDataLayer('coupon_disable', {
      from: this._source,
      coupon_name: upperCode
    });

    let cart;
    try {
      const resp = await fetch(_getCartUpdateUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          discount: remaining.join(',') || '',
          sections: fragmentName,
          sections_url: _getSectionsUrl()
        })
      });
      cart = await resp.json();
    } catch (_) {
      return;
    }

    try {
      localStorage.setItem(
        'cart:total_discount',
        String(cart.total_discount || 0)
      );
    } catch (_) {}

    this.dispatchEvent(
      new CustomEvent('cart:discount:removed', {
        bubbles: true,
        detail: {
          sections: cart.sections,
          totalDiscount: cart.total_discount,
          cart
        }
      })
    );
  }

  _showBannerError(msg) {
    const banner = this.querySelector('.cart-discount-input__error-banner');
    const field = this.querySelector('.cart-discount-input__error-field');
    const input = this._input();
    if (banner) {
      const text = banner.querySelector(
        '.cart-discount-input__error-banner-text'
      );
      if (text) text.textContent = msg;
      banner.classList.remove('tw-hidden');
      banner.classList.add('tw-flex');
    }
    if (field) field.classList.add('tw-hidden');
    if (input) input.classList.remove('cart-discount-input__input--error');
  }

  _showFieldError(msg) {
    const banner = this.querySelector('.cart-discount-input__error-banner');
    const field = this.querySelector('.cart-discount-input__error-field');
    const input = this._input();
    if (banner) {
      banner.classList.add('tw-hidden');
      banner.classList.remove('tw-flex');
    }
    if (field) {
      field.textContent = msg;
      field.classList.remove('tw-hidden');
    }
    if (input) input.classList.add('cart-discount-input__input--error');
  }

  _hideErrors() {
    const banner = this.querySelector('.cart-discount-input__error-banner');
    if (banner) {
      banner.classList.add('tw-hidden');
      banner.classList.remove('tw-flex');
    }
    this.querySelector('.cart-discount-input__error-field')?.classList.add(
      'tw-hidden'
    );
    this._input()?.classList.remove('cart-discount-input__input--error');
  }

  _setLoading(loading, btn) {
    if (!btn) return;
    btn.disabled = loading;
  }

  _input() {
    return this.querySelector('.cart-discount-input__input');
  }

  _applyBtn() {
    return this.querySelector('.cart-discount-input__apply-btn');
  }

  _pushDataLayer(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'custom_event',
      event_name: eventName,
      parameter: JSON.stringify(params)
    });
  }
}

if (!customElements.get('cart-discount-input')) {
  customElements.define('cart-discount-input', CartDiscountInput);
}
